import React, { useState, useEffect, createContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import "@/App.css";

// Import pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import FingerprintCapturePage from "@/pages/FingerprintCapturePage";
import MobileIDCapturePage from "@/pages/MobileIDCapturePage";
import AdminDashboard from "@/pages/AdminDashboard";
import ClientsPage from "@/pages/ClientsPage";
import ClientEditPage from "@/pages/ClientEditPage";
import ClientIntakePage from "@/pages/ClientIntakePage";
import ProfilePage from "@/pages/ProfilePage";
import LicensePage from "@/pages/LicensePage";
import PaymentPage from "@/pages/PaymentPage";
import ProfitCalculatorPage from "@/pages/ProfitCalculatorPage";
import ServicesPage from "@/pages/ServicesPage";
import FindProviderPage from "@/pages/FindProviderPage";
import ForVendorsPage from "@/pages/ForVendorsPage";
import LicenseAdminPage from "@/pages/LicenseAdminPage";
import PaymentHistoryPage from "@/pages/PaymentHistoryPage";
import ClientStatusPage from "@/pages/ClientStatusPage";

// Import session timeout components
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import SessionTimeoutModal from "@/components/SessionTimeoutModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios configuration
axios.defaults.baseURL = API;

// Auth Context
export const AuthContext = createContext(null);

// Session timeout wrapper component
function SessionTimeoutWrapper({ user, logout, children }) {
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Don't enable session timeout on public pages
  const isPublicPage = ['/', '/login', '/register', '/services', '/find-provider', '/for-vendors', '/pricing', '/calculator'].includes(location.pathname) || 
                       location.pathname.startsWith('/intake/') ||
                       location.pathname.startsWith('/status/') ||
                       location.pathname.startsWith('/payment/');
  const sessionEnabled = !!user && !isPublicPage;

  const handleTimeout = useCallback(() => {
    setShowWarning(false);
    logout();
    toast.error('Session expired due to inactivity', { duration: 5000 });
    navigate('/login');
  }, [logout, navigate]);

  const handleWarning = useCallback((remainingMinutes) => {
    setShowWarning(true);
  }, []);

  const { resetTimeout } = useSessionTimeout({
    onTimeout: handleTimeout,
    onWarning: handleWarning,
    enabled: sessionEnabled
  });

  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    resetTimeout();
    toast.success('Session extended', { duration: 2000 });
  }, [resetTimeout]);

  return (
    <>
      {children}
      <SessionTimeoutModal
        isOpen={showWarning}
        remainingMinutes={5}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleTimeout}
      />
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      // Verify token
      axios
        .get("/auth/me")
        .then((response) => {
          setUser(response.data);
        })
        .catch(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("lastActivityTime");
          delete axios.defaults.headers.common["Authorization"];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("lastActivityTime", Date.now().toString());
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("lastActivityTime");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <SessionTimeoutWrapper user={user} logout={logout}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/dashboard" />} />
            <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={user ? <DashboardPage /> : <Navigate to="/login" />} />
            <Route path="/clients" element={user ? <ClientsPage /> : <Navigate to="/login" />} />
            <Route path="/clients/:id" element={user ? <ClientEditPage /> : <Navigate to="/login" />} />
            <Route path="/capture" element={user ? <FingerprintCapturePage /> : <Navigate to="/login" />} />
            <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/login" />} />
            <Route path="/license" element={user ? <LicensePage /> : <Navigate to="/login" />} />
            <Route path="/pricing" element={<PaymentPage />} />
            <Route path="/calculator" element={<ProfitCalculatorPage />} />
            <Route path="/payment/success" element={<PaymentPage />} />
            <Route path="/payment/cancel" element={<PaymentPage />} />
            <Route path="/admin" element={user?.role === "admin" ? <AdminDashboard /> : <Navigate to="/dashboard" />} />
            {/* Public routes - no auth required */}
            <Route path="/intake/:token" element={<ClientIntakePage />} />
            <Route path="/status/:token" element={<ClientStatusPage />} />
            <Route path="/mobile-id/:token" element={<MobileIDCapturePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/find-provider" element={<FindProviderPage />} />
            <Route path="/for-vendors" element={<ForVendorsPage />} />
            <Route path="/admin/licenses" element={user?.role === "admin" ? <LicenseAdminPage /> : <Navigate to="/dashboard" />} />
            <Route path="/dashboard/payments" element={user ? <PaymentHistoryPage /> : <Navigate to="/login" />} />
          </Routes>
        </SessionTimeoutWrapper>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
